/**
 * Token icon/logo utilities.
 *
 * For well-known tokens, logos are served from the Solana token-list GitHub CDN
 * (verified stable URLs). For unknown mints passed directly we fall back to the
 * Jupiter image CDN. Unknown short symbols get an empty string → letter fallback.
 */

const TOKEN_LIST_BASE = 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet'
const JUP_IMG_BASE    = 'https://img.jup.ag/tokens'

/** Direct logo URLs for tokens not in the solana-labs token-list but with a known stable CDN URL. */
const DIRECT_LOGO_URLS: Record<string, string> = {
  'jupSoLaHXQiZZTSfEWMTRRgpnyFm8f6sZdosWBjx93v': 'https://static.jup.ag/jupSOL/icon.png',
}

const SYMBOL_TO_MINT: Record<string, string> = {
  SOL:     'So11111111111111111111111111111111111111112',
  USDC:    'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
  USDT:    'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
  mSOL:    'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So',
  jitoSOL: 'J1toso1uCk3RLmjorhTtrVwY9HJ7X8V9yYac6Y7kGCPn',
  jupSOL:  'jupSoLaHXQiZZTSfEWMTRRgpnyFm8f6sZdosWBjx93v',
  bSOL:    'bSo13r4TkiE4KumL71LsHTPpL2euBYLFx6h9HP3piy1',
  RAY:     '4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R',
  BONK:    'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263',
  JUP:     'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN',
  WIF:     'EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm',
  PYTH:    'HZ1JovNiVvGrGNiiYvEozEVgZ58xaU3RKwX8eACQBCt3',
}

/** Mints with confirmed logo.png entries in the solana-labs token-list repo. */
const KNOWN_MINTS_WITH_TOKEN_LIST_LOGO = new Set([
  'So11111111111111111111111111111111111111112',   // SOL
  'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', // USDC
  'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB', // USDT
  'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So', // mSOL
  'J1toso1uCk3RLmjorhTtrVwY9HJ7X8V9yYac6Y7kGCPn', // jitoSOL
  'bSo13r4TkiE4KumL71LsHTPpL2euBYLFx6h9HP3piy1',  // bSOL
  '4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R', // RAY
  'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263', // BONK
  'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN',  // JUP
  'EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm', // WIF
  'HZ1JovNiVvGrGNiiYvEozEVgZ58xaU3RKwX8eACQBCt3', // PYTH
])

/** Returns a logo image URL for the given token symbol or mint address. */
export function getTokenLogoUri(symbolOrMint: string): string {
  if (!symbolOrMint) return ''
  // Known symbol → resolve to mint first
  const mint = SYMBOL_TO_MINT[symbolOrMint] ?? symbolOrMint
  // Looks like a base58 mint address (≥32 chars, no spaces)
  if (mint.length >= 32 && !mint.includes('/')) {
    if (KNOWN_MINTS_WITH_TOKEN_LIST_LOGO.has(mint))
      return `${TOKEN_LIST_BASE}/${mint}/logo.png`
    if (DIRECT_LOGO_URLS[mint])
      return DIRECT_LOGO_URLS[mint]
    return `${JUP_IMG_BASE}/${mint}`
  }
  return ''
}
